import React, { useState } from 'react';
import {
  AlertTriangle,
  Bell,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Filter,
  Search,
  Calendar,
  User,
  Package,
  Shield,
  Zap,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Settings,
  Download,
  Archive,
  Trash2,
  MessageSquare,
  Phone,
  Mail,
  ExternalLink
} from 'lucide-react';
import { format, isToday, isYesterday, differenceInDays } from 'date-fns';

interface Alert {
  id: string;
  type: 'stock' | 'interaction' | 'compliance' | 's8' | 'expiry' | 'order' | 'system' | 'patient_safety';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  message: string;
  details: string;
  source: string;
  createdAt: string;
  updatedAt?: string;
  status: 'active' | 'acknowledged' | 'resolved' | 'dismissed';
  assignedTo?: string;
  relatedEntity?: {
    type: 'patient' | 'medication' | 'order' | 'supplier';
    id: string;
    name: string;
  };
  actions: AlertAction[];
  priority: number;
  autoResolve: boolean;
  escalationLevel: number;
}

interface AlertAction {
  id: string;
  label: string;
  type: 'primary' | 'secondary' | 'danger';
  action: string;
  requiresConfirmation?: boolean;
}

const Alerts: React.FC = () => {
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('active');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);

  // Comprehensive alert data
  const alerts: Alert[] = [
    {
      id: 'alert-001',
      type: 'stock',
      severity: 'critical',
      title: 'Critical Stock Level - Rosuvastatin 20mg',
      message: 'Only 15 units remaining. Multiple aged care orders pending.',
      details: 'Current stock: 15 units. Pending orders require 45 units. Supplier lead time: 3 days. Alternative suppliers available.',
      source: 'Inventory Management System',
      createdAt: '2024-09-15T09:15:00Z',
      status: 'active',
      assignedTo: 'Inventory Manager',
      relatedEntity: {
        type: 'medication',
        id: 'med-rosuvastatin-20',
        name: 'Rosuvastatin 20mg'
      },
      actions: [
        { id: 'action-001', label: 'Order from Sigma Healthcare', type: 'primary', action: 'order_stock' },
        { id: 'action-002', label: 'Request Transfer', type: 'secondary', action: 'request_transfer' },
        { id: 'action-003', label: 'Contact Supplier', type: 'secondary', action: 'contact_supplier' }
      ],
      priority: 1,
      autoResolve: false,
      escalationLevel: 2
    },
    {
      id: 'alert-002',
      type: 'interaction',
      severity: 'high',
      title: 'Drug Interaction - Warfarin + Aspirin',
      message: 'Major interaction detected for Margaret Wilson (Room A101)',
      details: 'Warfarin 5mg daily + Aspirin 100mg daily. Increased bleeding risk. Patient has history of GI bleeding. INR last checked 2024-09-10: 2.8.',
      source: 'Clinical Decision Support',
      createdAt: '2024-09-15T08:30:00Z',
      status: 'active',
      assignedTo: 'Clinical Pharmacist',
      relatedEntity: {
        type: 'patient',
        id: 'patient-001',
        name: 'Margaret Wilson'
      },
      actions: [
        { id: 'action-004', label: 'Review Patient', type: 'primary', action: 'review_patient' },
        { id: 'action-005', label: 'Contact Prescriber', type: 'secondary', action: 'contact_prescriber' },
        { id: 'action-006', label: 'Override with Note', type: 'danger', action: 'override_interaction', requiresConfirmation: true }
      ],
      priority: 2,
      autoResolve: false,
      escalationLevel: 1
    },
    {
      id: 'alert-003',
      type: 's8',
      severity: 'high',
      title: 'S8 Witness Required - OxyContin Dispensing',
      message: 'S8 medication requires pharmacist witness for aged care order AC-2024-0915-001',
      details: 'OxyContin 10mg x 56 tablets for Dorothy Chen. Requires pharmacist witness as per Victorian regulations. Order cannot proceed without witness.',
      source: 'S8 Compliance System',
      createdAt: '2024-09-15T10:45:00Z',
      status: 'active',
      assignedTo: 'Pharmacist in Charge',
      relatedEntity: {
        type: 'order',
        id: 'order-001',
        name: 'AC-2024-0915-001'
      },
      actions: [
        { id: 'action-007', label: 'Schedule Witness', type: 'primary', action: 'schedule_witness' },
        { id: 'action-008', label: 'View S8 Register', type: 'secondary', action: 'view_s8_register' }
      ],
      priority: 3,
      autoResolve: true,
      escalationLevel: 1
    },
    {
      id: 'alert-004',
      type: 'expiry',
      severity: 'medium',
      title: 'Medications Expiring Soon',
      message: '12 medications expiring within 30 days',
      details: 'Paracetamol 500mg (40 units) expires 24/12/2024, Ventolin HFA (22 units) expires 20/03/2025, and 10 other medications.',
      source: 'Expiry Management System',
      createdAt: '2024-09-15T07:00:00Z',
      status: 'active',
      assignedTo: 'Inventory Manager',
      actions: [
        { id: 'action-009', label: 'View Expiry Report', type: 'primary', action: 'view_expiry_report' },
        { id: 'action-010', label: 'Schedule Returns', type: 'secondary', action: 'schedule_returns' }
      ],
      priority: 4,
      autoResolve: false,
      escalationLevel: 0
    },
    {
      id: 'alert-005',
      type: 'compliance',
      severity: 'medium',
      title: 'CPD Hours Deadline Approaching',
      message: '15 hours of CPD required by December 31, 2024',
      details: 'Current progress: 25/40 hours completed. Remaining time: 107 days. Recommended to complete 2-3 hours per month.',
      source: 'Professional Development Tracker',
      createdAt: '2024-09-15T06:00:00Z',
      status: 'active',
      assignedTo: 'All Pharmacists',
      actions: [
        { id: 'action-011', label: 'View CPD Progress', type: 'primary', action: 'view_cpd_progress' },
        { id: 'action-012', label: 'Find CPD Activities', type: 'secondary', action: 'find_cpd_activities' }
      ],
      priority: 5,
      autoResolve: false,
      escalationLevel: 0
    },
    {
      id: 'alert-006',
      type: 'order',
      severity: 'high',
      title: 'Urgent Order Deadline',
      message: 'NDIS order NDIS-2024-0915-002 required by tomorrow',
      details: 'Order contains cold chain medications. Supplier lead time is 4 days but patient needs medication urgently. Alternative suppliers may be available.',
      source: 'Order Management System',
      createdAt: '2024-09-15T11:20:00Z',
      status: 'active',
      assignedTo: 'Order Coordinator',
      relatedEntity: {
        type: 'order',
        id: 'order-002',
        name: 'NDIS-2024-0915-002'
      },
      actions: [
        { id: 'action-013', label: 'Check Alternative Suppliers', type: 'primary', action: 'check_suppliers' },
        { id: 'action-014', label: 'Request Urgent Delivery', type: 'secondary', action: 'request_urgent_delivery' },
        { id: 'action-015', label: 'Contact Patient', type: 'secondary', action: 'contact_patient' }
      ],
      priority: 2,
      autoResolve: false,
      escalationLevel: 1
    },
    {
      id: 'alert-007',
      type: 'patient_safety',
      severity: 'critical',
      title: 'Allergy Alert - Penicillin Prescribed',
      message: 'Amoxicillin prescribed for patient with documented penicillin allergy',
      details: 'Patient: Robert Thompson (Room A102). Documented allergy: Penicillin (severe reaction). Prescribed: Amoxicillin 500mg TDS. Prescriber: Dr. Johnson.',
      source: 'Clinical Safety System',
      createdAt: '2024-09-15T13:45:00Z',
      status: 'active',
      assignedTo: 'Clinical Pharmacist',
      relatedEntity: {
        type: 'patient',
        id: 'patient-002',
        name: 'Robert Thompson'
      },
      actions: [
        { id: 'action-016', label: 'Contact Prescriber Immediately', type: 'danger', action: 'contact_prescriber_urgent' },
        { id: 'action-017', label: 'Suggest Alternative', type: 'primary', action: 'suggest_alternative' },
        { id: 'action-018', label: 'Document Intervention', type: 'secondary', action: 'document_intervention' }
      ],
      priority: 1,
      autoResolve: false,
      escalationLevel: 3
    },
    {
      id: 'alert-008',
      type: 'system',
      severity: 'low',
      title: 'System Backup Completed',
      message: 'Daily system backup completed successfully',
      details: 'Backup completed at 02:00 AM. All data backed up successfully. Next backup scheduled for tomorrow at 02:00 AM.',
      source: 'System Administration',
      createdAt: '2024-09-15T02:00:00Z',
      status: 'resolved',
      actions: [],
      priority: 10,
      autoResolve: true,
      escalationLevel: 0
    }
  ];

  const filteredAlerts = alerts.filter(alert => {
    const matchesSeverity = selectedSeverity === 'all' || alert.severity === selectedSeverity;
    const matchesType = selectedType === 'all' || alert.type === selectedType;
    const matchesStatus = selectedStatus === 'all' || alert.status === selectedStatus;
    const matchesSearch = searchTerm === '' || 
      alert.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alert.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alert.source.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSeverity && matchesType && matchesStatus && matchesSearch;
  }).sort((a, b) => {
    // Sort by priority (lower number = higher priority), then by creation date
    if (a.priority !== b.priority) {
      return a.priority - b.priority;
    }
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case 'high': return <AlertTriangle className="h-4 w-4 text-orange-600" />;
      case 'medium': return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'low': return <Bell className="h-4 w-4 text-blue-600" />;
      default: return <Bell className="h-4 w-4 text-gray-600" />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'stock': return <Package className="h-4 w-4" />;
      case 'interaction': return <Zap className="h-4 w-4" />;
      case 'compliance': return <Shield className="h-4 w-4" />;
      case 's8': return <Shield className="h-4 w-4" />;
      case 'expiry': return <Calendar className="h-4 w-4" />;
      case 'order': return <Package className="h-4 w-4" />;
      case 'patient_safety': return <User className="h-4 w-4" />;
      case 'system': return <Settings className="h-4 w-4" />;
      default: return <Bell className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-red-100 text-red-800';
      case 'acknowledged': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'dismissed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    if (isToday(date)) {
      return `Today ${format(date, 'HH:mm')}`;
    } else if (isYesterday(date)) {
      return `Yesterday ${format(date, 'HH:mm')}`;
    } else {
      const days = differenceInDays(new Date(), date);
      return `${days} days ago`;
    }
  };

  const alertStats = {
    total: alerts.length,
    active: alerts.filter(a => a.status === 'active').length,
    critical: alerts.filter(a => a.severity === 'critical' && a.status === 'active').length,
    high: alerts.filter(a => a.severity === 'high' && a.status === 'active').length,
    resolved: alerts.filter(a => a.status === 'resolved').length
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Alert Management
          </h1>
          <p className="text-gray-600 mt-2">Monitor and manage all system alerts and notifications</p>
        </div>
        <div className="flex space-x-3">
          <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2">
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
          <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center space-x-2">
            <Settings className="h-4 w-4" />
            <span>Settings</span>
          </button>
          <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center space-x-2">
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Alert Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 text-center">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Bell className="h-6 w-6 text-white" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{alertStats.total}</p>
          <p className="text-gray-600 text-sm">Total Alerts</p>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 text-center">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mx-auto mb-3">
            <AlertTriangle className="h-6 w-6 text-white" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{alertStats.active}</p>
          <p className="text-gray-600 text-sm">Active Alerts</p>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 text-center">
          <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center mx-auto mb-3">
            <AlertTriangle className="h-6 w-6 text-white" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{alertStats.critical}</p>
          <p className="text-gray-600 text-sm">Critical</p>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 text-center">
          <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Clock className="h-6 w-6 text-white" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{alertStats.high}</p>
          <p className="text-gray-600 text-sm">High Priority</p>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 text-center">
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mx-auto mb-3">
            <CheckCircle className="h-6 w-6 text-white" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{alertStats.resolved}</p>
          <p className="text-gray-600 text-sm">Resolved</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search alerts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={selectedSeverity}
            onChange={(e) => setSelectedSeverity(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="stock">Stock</option>
            <option value="interaction">Drug Interactions</option>
            <option value="compliance">Compliance</option>
            <option value="s8">S8 Controlled</option>
            <option value="expiry">Expiry</option>
            <option value="order">Orders</option>
            <option value="patient_safety">Patient Safety</option>
            <option value="system">System</option>
          </select>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
          </select>

          <button 
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Filter className="h-4 w-4" />
            <span>More Filters</span>
          </button>
        </div>
      </div>

      {/* Alerts List */}
      <div className="space-y-4">
        {filteredAlerts.map((alert) => (
          <div key={alert.id} className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start space-x-4 flex-1">
                <div className="flex items-center space-x-2">
                  {getSeverityIcon(alert.severity)}
                  {getTypeIcon(alert.type)}
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{alert.title}</h3>
                    <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getSeverityColor(alert.severity)}`}>
                      {alert.severity.toUpperCase()}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(alert.status)}`}>
                      {alert.status.toUpperCase()}
                    </span>
                  </div>
                  
                  <p className="text-gray-700 mb-2">{alert.message}</p>
                  
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>{alert.source}</span>
                    <span>•</span>
                    <span>{getTimeAgo(alert.createdAt)}</span>
                    {alert.assignedTo && (
                      <>
                        <span>•</span>
                        <span>Assigned to: {alert.assignedTo}</span>
                      </>
                    )}
                    {alert.relatedEntity && (
                      <>
                        <span>•</span>
                        <span>{alert.relatedEntity.type}: {alert.relatedEntity.name}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button 
                  onClick={() => setSelectedAlert(alert)}
                  className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                >
                  <Eye className="h-4 w-4" />
                </button>
                {alert.status === 'active' && (
                  <button className="p-2 text-green-600 hover:text-green-800 hover:bg-green-100 rounded-lg">
                    <CheckCircle className="h-4 w-4" />
                  </button>
                )}
                <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                  <Archive className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Alert Actions */}
            {alert.actions.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
                {alert.actions.map((action) => (
                  <button
                    key={action.id}
                    className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${
                      action.type === 'primary' ? 'bg-purple-600 text-white hover:bg-purple-700' :
                      action.type === 'danger' ? 'bg-red-600 text-white hover:bg-red-700' :
                      'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredAlerts.length === 0 && (
        <div className="text-center py-12">
          <Bell className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No alerts found</h3>
          <p className="mt-1 text-sm text-gray-500">
            No alerts match your current filters.
          </p>
        </div>
      )}

      {/* Alert Detail Modal */}
      {selectedAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                {getSeverityIcon(selectedAlert.severity)}
                <h3 className="text-xl font-bold text-gray-900">{selectedAlert.title}</h3>
                <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getSeverityColor(selectedAlert.severity)}`}>
                  {selectedAlert.severity.toUpperCase()}
                </span>
              </div>
              <button
                onClick={() => setSelectedAlert(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Message</h4>
                <p className="text-gray-700">{selectedAlert.message}</p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Details</h4>
                <p className="text-gray-700">{selectedAlert.details}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Source</h4>
                  <p className="text-gray-700">{selectedAlert.source}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Created</h4>
                  <p className="text-gray-700">{format(new Date(selectedAlert.createdAt), 'dd/MM/yyyy HH:mm')}</p>
                </div>
              </div>
              
              {selectedAlert.assignedTo && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Assigned To</h4>
                  <p className="text-gray-700">{selectedAlert.assignedTo}</p>
                </div>
              )}
              
              {selectedAlert.relatedEntity && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Related Entity</h4>
                  <p className="text-gray-700">
                    {selectedAlert.relatedEntity.type}: {selectedAlert.relatedEntity.name}
                  </p>
                </div>
              )}
            </div>
            
            {selectedAlert.actions.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t border-gray-200">
                {selectedAlert.actions.map((action) => (
                  <button
                    key={action.id}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      action.type === 'primary' ? 'bg-purple-600 text-white hover:bg-purple-700' :
                      action.type === 'danger' ? 'bg-red-600 text-white hover:bg-red-700' :
                      'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Alerts;
